<template>
    <v-container fluid class="ml">
        <!-- 頁首：標題、儲存空間、主要按鈕 -->
        <div class="ml-head">
            <div class="ml-head-txt">
                <h1 class="ml-title">{{ $t('media.library.title') }}</h1>
                <div class="ml-sub">{{ $t('media.library.subtitle') }}</div>
            </div>

            <div class="ml-quota">
                <div class="ml-quota-label">{{ $t('media.library.quota_label') }}</div>
                <div class="ml-quota-value">{{ quotaText }}</div>
                <!-- 進度條自己畫而不用 v-progress-linear：設計要 5px 高、3px 圓角，
                     而那個元件會帶自己的一層樣式，蓋掉它比自己畫還麻煩。 -->
                <div class="ml-quota-bar">
                    <div class="ml-quota-fill"
                        :class="{ 'ml-quota-fill--full': quotaPercent >= 90 }"
                        :style="{ width: quotaPercent + '%' }"></div>
                </div>
            </div>
        </div>

        <!-- 大類 tab。手寫而不用 v-tabs：設計要 16px bold 加 3px 底線與筆數，
             而 VTabs 的 slider 與字級都得用 !important 蓋掉，蓋完也還是它的 DOM。 -->
        <div class="ml-groups">
            <button v-for="(g, gi) in groups" :key="g.key" type="button" class="ml-group"
                :class="{ 'ml-group--on': gi === groupIndex }" @click="selectGroup(gi)">
                {{ $t(`media.group.${g.key}`) }}
                <span class="ml-group-n" :class="{ 'ml-group-n--on': gi === groupIndex }">
                    {{ groupCount(g) }}
                </span>
            </button>
        </div>

        <!-- 第二排：細分類 chips。一定有一個選中，換大類時自動選第一個。
             影片那一類的 chips 是依「來源」而不是 purpose 分的。 -->
        <div class="ml-chips">
            <button v-for="(c, ci) in currentGroup.chips" :key="ci" type="button" class="ml-chip"
                :class="{ 'ml-chip--on': ci === chipIndex }" @click="selectChip(ci)">
                {{ chipLabel(c) }}
                <span class="ml-chip-n">{{ chipCount(c) }}</span>
            </button>
        </div>

        <!-- 工具列。一定要放在 .ml-body 外面：它一度在「有資料」的分支裡，於是
             「搜不到東西」會連搜尋框一起消失，老師清不掉關鍵字、卡在一個看起來
             像空分類的畫面。 -->
        <div class="ml-toolbar">
            <v-text-field v-model="keyword" dense hide-details outlined clearable
                prepend-inner-icon="mdi-magnify" class="ml-search"
                :placeholder="$t('media.library.search_hint')"
                @keyup.enter="reload(1)" @click:clear="reload(1)" />

            <v-select v-model="visibility" :items="visibilityItems" dense hide-details outlined
                class="ml-sel" :label="$t('media.library.visibility_label')"
                @change="reload(1)" />

            <v-select v-model="order" :items="orderItems" dense hide-details outlined
                class="ml-sel" :label="$t('media.library.order_label')" @change="reload(1)" />

            <div class="ml-count">{{ $t('media.library.count', { n: total }) }}</div>

            <!-- 全頁只有兩處按鈕：這一顆與每一列右側。
                 字表不是「上傳」而是「建立」，所以這一顆的文字會換；它是同一個位置的
                 同一顆主要按鈕，只是目標分類不同。
                 因為 chips 一定有一個選中，currentPurpose 永遠是具體的值，
                 所以這一顆永遠知道要建成哪一種素材。
                 v-if 與 v-else-if 必須相鄰，中間不要插任何元素。 -->
            <v-btn v-if="canUpload" depressed color="#1A73E8"
                class="normal-case ml-primary" @click="uploadOpen = true">
                {{ $t('media.library.upload') }}
            </v-btn>
            <v-btn v-else-if="canCreate" depressed color="#1A73E8"
                class="normal-case ml-primary" @click="openWordlist(null)">
                {{ $t('media.wordlist.create') }}
            </v-btn>
        </div>

        <div class="ml-body">
            <div v-if="isLoading" class="ml-state">
                <v-progress-circular indeterminate size="30" color="#1A73E8" />
            </div>

            <div v-else-if="loadError" class="ml-state">
                <v-icon size="34" color="#C5221F">mdi-alert-circle-outline</v-icon>
                <div class="ml-state-msg">{{ loadError }}</div>
                <button type="button" class="ml-btn ml-btn--open" @click="reload(page)">
                    {{ $t('media.picker.retry') }}
                </button>
            </div>

            <!-- 有篩選條件時不能說「這個分類還沒有你的素材」，那是在騙人：素材可能就在
                 下一個關鍵字裡。分成兩種空狀態，而且有篩選時給清除的出口。 -->
            <div v-else-if="!items.length && hasFilter" class="ml-state">
                <v-icon size="34" color="#80868B">mdi-magnify-close</v-icon>
                <div class="ml-state-msg">{{ $t('media.library.empty_filtered') }}</div>
                <button type="button" class="ml-btn ml-btn--open" @click="clearFilter">
                    {{ $t('media.library.clear_filter') }}
                </button>
            </div>

            <!-- 這裡沒有上傳按鈕：主要按鈕在工具列最右，空狀態再放一顆等於同一個
                 動作有兩個入口。
                 只有一句文案：七個分類每一個都是「可上傳」或「可建立」其中之一
                 （字表在 F13 之後變成可建立），所以原本的「這個分類需要由我們建立」
                 那個分支永遠不成立，2026-09-09 連同 i18n 一起移除。
                 日後真的加了一種既不能上傳也不能建立的分類，這裡要一起改。 -->
            <div v-else-if="!items.length" class="ml-state">
                <v-icon size="34" color="#80868B">mdi-image-off-outline</v-icon>
                <div class="ml-state-msg">{{ $t('media.library.empty') }}</div>
            </div>

            <template v-else>
                <div class="ml-thead mt-6">
                    <div class="ml-th ml-col-thumb">{{ $t('media.library.col_thumb') }}</div>
                    <div class="ml-th ml-col-info">{{ $t('media.library.col_title') }}</div>
                    <div class="ml-th ml-col-vis">{{ $t('media.library.col_visibility') }}</div>
                    <div class="ml-th ml-col-acts">{{ $t('media.library.col_actions') }}</div>
                </div>

                <div class="ml-list">
                    <div v-for="item in items" :key="item.media_id" class="ml-row">
                        <div class="ml-col-thumb">
                            <!-- 縮圖的算繪邏輯集中在 MediaThumb（2026-09-07 抽出）：
                                 圖片看 url、影片與 PDF 看 poster_url、字表列單字、熱區畫框。
                                 這一頁維持 cover（裁切填滿）、不畫熱區框、不列單字、
                                 不內嵌 PDF——72x54 的框裡那些都看不清楚。 -->
                            <div class="ml-thumb">
                                <MediaThumb :media="item" fit="cover" :icon-size="24" />
                            </div>
                        </div>

                        <div class="ml-col-info">
                            <!-- 檔案大小與上傳日期放在 hover 提示裡。
                                 YouTube 沒有 byte_size，所以提示要說出「不佔容量」，
                                 否則老師看到「沒有大小」會以為資料壞了。 -->
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <div class="ml-name" v-bind="attrs" v-on="on">
                                        {{ item.title }}
                                        <!-- 這兩個狀態標籤不能拿掉。設計只提到把分享狀態與
                                             檔案大小那排灰標籤移走，但已下架與轉檔中的列
                                             長得跟正常的一模一樣，沒有標籤的話老師分不出來
                                             哪一份學生看不到。 -->
                                        <span v-if="isBanned(item)"
                                            class="ml-flag ml-flag--bad">
                                            {{ $t('media.library.banned_tag') }}
                                        </span>
                                        <span v-else-if="item.status === 'delisted'"
                                            class="ml-flag ml-flag--bad">
                                            {{ $t('media.library.delisted_tag') }}
                                        </span>
                                        <span v-else-if="item.process_status !== 'ready'"
                                            class="ml-flag ml-flag--warn">
                                            {{ $t(`media.process.${item.process_status}`) }}
                                        </span>
                                    </div>
                                </template>
                                <div class="ml-tip">
                                    <div v-if="item.byte_size">{{ sizeOf(item) }}</div>
                                    <div v-else-if="providerOf(item) === 'youtube'">
                                        {{ $t('media.library.no_size_youtube') }}
                                    </div>
                                    <div v-if="item.create_date">
                                        {{ $t('media.library.uploaded_at', { d: dateOf(item) }) }}
                                    </div>
                                </div>
                            </v-tooltip>

                            <div v-if="item.description" class="ml-desc">{{ item.description }}</div>

                            <!-- 平台下架的原因一定要顯示。老師**不能自己上架**，
                                 原因是他唯一的依據——不顯示的話他只看到「已被平台下架」，
                                 既救不回來也不知道要怎麼換，只能來問客服。 -->
                            <div v-if="isBanned(item)" class="ml-note ml-note--bad">
                                {{ $t('media.library.banned_note') }}
                                <template v-if="banReasonOf(item)">
                                    {{ banReasonOf(item) }}
                                </template>
                            </div>

                            <!-- 退回理由一定要顯示出來。第一期沒有審核介面（決策 21），
                                 這個值是我們手動寫進資料庫的；不顯示的話老師只看到
                                 「已退回」卻不知道要改什麼。 -->
                            <div v-if="item.review_status === 'rejected' && item.reject_reason"
                                class="ml-note ml-note--bad">
                                {{ $t('media.review.reason', { reason: item.reject_reason }) }}
                            </div>

                            <!-- 送審中要同時說出「還沒成為公開素材」與「你自己仍可使用」
                                 （規格 7.3、ADR 0005）。只寫「審核中」會讓老師不敢用。 -->
                            <div v-if="item.review_status === 'pending'" class="ml-note">
                                {{ $t('media.library.pending_note') }}
                            </div>

                            <!-- 轉檔失敗要給出口。重試需要一支目前不存在的端點，
                                 所以只說明狀況，不給一個會 404 的按鈕。 -->
                            <div v-if="item.process_status === 'failed'" class="ml-note ml-note--bad">
                                {{ $t('media.library.failed_note') }}
                            </div>
                        </div>

                        <!-- 這一欄講的是四種狀態而不是 share_scope 的兩個值：public 底下
                             還分「審核中／分享中／已退回」，而除了分享中之外的兩種
                             別人根本看不到。與工具列的篩選用同一組字。 -->
                        <div class="ml-col-vis">
                            <span class="ml-dot"></span>
                            <span class="ml-vis-txt">
                                {{ $t(`media.visibility.${visibilityOf(item)}`) }}
                            </span>
                        </div>

                        <!-- 操作欄用 min-width 而不是固定 300px。找不同的圖有四顆按鈕
                             （看標註的區域／編輯／編輯熱區／下架），加起來超過 300px，
                             固定寬度會裁掉最後一顆。min-width 讓同一個分類的列仍然對齊
                             （按鈕組相同），只有需要時才變寬。 -->
                        <div class="ml-col-acts">
                            <!-- 熱區圖走站內預覽，不開新分頁：新分頁開的是原始圖檔，
                                 看不到任何熱區。 -->
                            <button v-if="item.purpose === 'hotspot' && item.url" type="button"
                                class="ml-btn ml-btn--open" @click="openPreview(item)">
                                {{ $t('media.hotspot.preview_action') }}
                            </button>
                            <!-- 字表沒有 url，所以兩個分支都不出現（給連結只會開空白頁）。 -->
                            <a v-else-if="item.url" :href="item.url" target="_blank"
                                rel="noopener" class="ml-btn ml-btn--open">
                                {{ $t('media.picker.open') }}
                            </a>
                            <button type="button" class="ml-btn" @click="openEdit(item)">
                                {{ $t('media.library.edit') }}
                            </button>
                            <!-- 只有字表有這一顆：它是唯一內容可以就地改變的素材
                                 （其他素材的內容是檔案，換檔案等於換一筆）。
                                 也是唯一一顆可能失敗的儲存按鈕：改字表會影響已經在用它的
                                 題目，後端會重驗那些題目的難度設定。 -->
                            <button v-if="item.kind === 'wordlist'" type="button" class="ml-btn"
                                @click="openWordlist(item)">
                                {{ $t('media.wordlist.edit_words') }}
                            </button>
                            <!-- 只有找不同圖，用 purpose 判斷（kind 是 image 分不出來）。
                                 這一頁只列自己的素材，所以不用再查 is_mine。 -->
                            <button v-if="item.purpose === 'hotspot'" type="button" class="ml-btn"
                                @click="openHotspot(item)">
                                {{ $t('media.hotspot.edit_action') }}
                            </button>
                            <!-- 文案不能寫「刪除」或「移除」（ADR 0007）：檔案不會從網路上
                                 消失。也不能寫「不影響已出題的題目」，下架會停用那些題目
                                 （ADR 0009）。
                                 下架一定要走 MediaDelistDialog，不能直接呼叫 service：
                                 那個對話框負責先查影響範圍並要求打勾，而後端沒帶 confirmed
                                 會回 409。
                                 灰框紅字而不是紅框：四列都是紅粗框時，那個「危險」的訊號
                                 會因為到處都是而失效。hover 才變紅底。 -->
                            <!-- ⚠⚠ 三態，不是兩態：
                                 active   → 「下架」
                                 delisted → 「重新上架」（自己下架的，自己救得回來）
                                 banned   → **兩顆都不給**。他不能上架（後端 relist 回 403），
                                            也不需要再下架（已經下架了）。
                                 這一顆原本的條件是 `status !== 'delisted'`，所以 banned
                                 會顯示「下架」——按下去對一份已經下架的素材再下架一次。 -->
                            <button v-if="item.status === 'active'" type="button"
                                class="ml-btn ml-btn--danger" @click="openDelist(item)">
                                {{ $t('media.library.delist') }}
                            </button>
                            <!-- 已下架的那一列換成「重新上架」。這一列看得到的前提是清單帶了
                                 includeDelisted（預設查詢只撈 active），否則素材一下架就從
                                 畫面消失、老師再也找不到它。 -->
                            <button v-else-if="item.status === 'delisted'" type="button"
                                class="ml-btn ml-btn--ok"
                                :disabled="relistingId === item.media_id"
                                @click="doRelist(item)">
                                {{ $t('media.library.relist') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="ml-foot">
                    <v-pagination v-if="pageCount > 1" v-model="page" :length="pageCount"
                        :total-visible="7" color="#1A73E8" @input="reload" />
                </div>
            </template>
        </div>

        <!-- 說明。每個分類都固定顯示：這兩顆按鈕在每一個分類都在，而它們的後果
             （停用別人的題目／自動恢復）不是按鈕名稱看得出來的。
             單一灰底、全部同色：原本紅橘混色，讓「下架會停用題目」這句真正重要的話
             跟旁邊的說明搶同一個視覺層級，結果三句都不顯眼。 -->
        <div class="ml-legend">
            <span class="ml-legend-icon">i</span>
            <div class="ml-legend-txt">
                <div>
                    {{ $t('media.library.delist') }}：{{ $t('media.library.delist_hint') }}
                    {{ $t('media.library.delist_hint_warn') }}
                </div>
                <div>
                    {{ $t('media.library.relist') }}：{{ $t('media.library.relist_hint') }}
                </div>
                <!-- 講清楚通知怎麼走：受影響的老師會收到站內訊息與 email。
                     這一句原本寫的是「還不會通知」，B12（2026-09-09）上線後就變成錯的。 -->
                <div>{{ $t('media.delist.notify_note') }}</div>
            </div>
        </div>

        <!-- 與題目編輯器共用同一個對話框：分享範圍那段文案有兩條硬規則
             （ADR 0005 的送審說明、ADR 0007 的 private 措辭），各寫一份遲早只改到一邊。 -->
        <MediaEditDialog :show.sync="editOpen" :media="editing" @saved="onEdited" />

        <MediaUploadDialog :show.sync="uploadOpen" :purpose="currentPurpose"
            :source="currentProvider === 'youtube' ? 'youtube' : 'file'"
            @uploaded="onUploaded" />
        <!-- 建立與編輯共用同一個對話框：media 給了就是編輯模式。
             沒有 v-if——editingWordlist 為 null 時就是建立模式，而 purpose 已經由
             chip 決定了。 -->
        <WordlistDialog :show.sync="wordlistOpen" :purpose="currentPurpose"
            :media="editingWordlist" @created="onUploaded" @saved="onEdited" />
        <HotspotEditDialog :show.sync="hotspotOpen" :media="editingHotspot" @saved="onEdited" />
        <HotspotPreviewDialog :show.sync="previewOpen" :media="previewItem" />
        <!-- 下架的二次確認。它自己會去查影響範圍，查不到就不讓下架。 -->
        <MediaDelistDialog :show.sync="delistOpen" :media="delistItem" @delisted="onDelisted" />
        <SnackMsg ref="snack" />
    </v-container>
</template>

<script>
import { mediaServices } from '@/services/media';
import MediaUploadDialog from '@/components/console/diary/media/MediaUploadDialog';
import MediaEditDialog from '@/components/console/diary/media/MediaEditDialog';
import WordlistDialog from '@/components/console/diary/media/WordlistDialog';
import HotspotEditDialog from '@/components/console/diary/media/HotspotEditDialog';
import HotspotPreviewDialog from '@/components/console/diary/media/HotspotPreviewDialog';
import MediaDelistDialog from '@/components/console/diary/media/MediaDelistDialog';
import MediaThumb from '@/components/console/diary/media/MediaThumb';
import SnackMsg from '@/components/console/diary/component/SnackMsg';
import {
    isUploadable, isCreatable, USER_QUOTA_GB, providerOf,
    isBlocked, isBanned, banReasonOf,
} from './mediaConstants';

/**
 * 四個大類，以及每一類底下的細分類 chips。
 *
 * chips 一定有一個選中（換大類時自動選第一個），這是刻意的設計決定：
 * GET /diary/media 的 purpose 是必填單值，沒有「列出多個分類」。允許「都不選等於
 * 該大類全部」的話，圖像那一類要一次查 3 個 purpose，而分頁必須在合併之後才算，
 * 前端合不了。而且「上傳素材」需要一個具體的 purpose（封面圖、圖片、找不同圖的
 * 驗證與寫入流程都不同），沒選 chip 就不知道要建成哪一種。
 *
 * 影片那一類的 chips 不是 purpose 而是來源（provider），所以 chip 的筆數要讀
 * counts 的 video_uploaded / video_youtube 而不是 counts[purpose]。
 *
 * 學習單只有一個 chip。留著是為了四個大類的版面一致（都有第二排），
 * 它永遠選中、按下去沒有變化。
 */
const GROUPS = [
    {
        key: 'image',
        purposes: ['image', 'cover', 'hotspot'],
        chips: [{ purpose: 'image' }, { purpose: 'cover' }, { purpose: 'hotspot' }],
    },
    {
        key: 'video',
        purposes: ['video'],
        chips: [
            { purpose: 'video', provider: 'uploaded' },
            { purpose: 'video', provider: 'youtube' },
        ],
    },
    { key: 'pdf', purposes: ['pdf'], chips: [{ purpose: 'pdf' }] },
    {
        key: 'wordlist',
        purposes: ['wordsearch', 'colorword'],
        chips: [{ purpose: 'wordsearch' }, { purpose: 'colorword' }],
    },
];

const PER_PAGE = 20;

/// 這四個值必須與後端的 visibility 值域一字不差（VISIBILITY_*）：下拉的值是直接
/// 送出去的。空字串等於不限。
const VISIBILITIES = ['', 'private', 'pending', 'shared', 'rejected'];
/// 同上，對應後端的 ORDER_*。沒有「使用次數」——那個數字目前不存在（要 join
/// diary_quest_media 統計），2026-09-08 決定先不做。
const ORDERS = ['latest', 'title', 'size'];

/**
 * 「我的素材」管理頁（F10）— 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md 7.3。
 *
 * 下架的效果是「停用」而不是刪除（ADR 0009）：用到它的題目會被停 status 到 -2，
 * 重新上架時自動恢復。所以這一頁的查詢帶 includeDelisted: true——不帶的話素材一下架
 * 就從畫面消失，老師再也找不到它、也就沒辦法上架回來。
 *
 * 轉檔失敗沒有「重試」按鈕：那需要一支目前不存在的端點。先顯示狀況說明。
 *
 * 分類筆數走 GET /diary/media/counts（一次回全部分類），而不是為 7 個分類各打一次
 * request。它與清單共用同一份 WHERE，所以 chip 的數字與清單內容一定對得上。
 */
export default {
    name: 'MediaLibrary',
    components: {
        MediaUploadDialog, MediaEditDialog, WordlistDialog, HotspotEditDialog,
        HotspotPreviewDialog, MediaDelistDialog, MediaThumb, SnackMsg,
    },
    data() {
        return {
            groups: GROUPS,
            groupIndex: 0,
            chipIndex: 0,
            keyword: '',
            visibility: '',
            order: 'latest',
            /// `{ counts: {purpose: n}, video_youtube, video_uploaded }`。
            /// 沒有資料的分類不會出現在 counts 裡（GROUP BY 的結果），要當 0。
            counts: null,
            items: [],
            total: 0,
            page: 1,
            isLoading: true,
            loadError: '',
            quota: null,
            uploadOpen: false,
            editOpen: false,
            editing: null,
            wordlistOpen: false,
            /// null 是建立模式；有值是編輯那一份字表
            editingWordlist: null,
            hotspotOpen: false,
            editingHotspot: null,
            previewOpen: false,
            previewItem: null,
            delistOpen: false,
            delistItem: null,
            /// 正在重新上架的那一筆，用來 disable 那顆按鈕（防連點）
            relistingId: null,
        };
    },
    computed: {
        currentGroup() {
            return GROUPS[this.groupIndex];
        },
        currentChip() {
            return this.currentGroup.chips[this.chipIndex] || this.currentGroup.chips[0];
        },
        currentPurpose() {
            return this.currentChip.purpose;
        },
        currentProvider() {
            return this.currentChip.provider || null;
        },
        /** 有沒有在篩。空狀態的文案與「清除」的出口都靠它。 */
        hasFilter() {
            return !!this.keyword || !!this.visibility;
        },
        canUpload() {
            return isUploadable(this.currentPurpose);
        },
        /**
         * 這個分類能不能在介面上建立（目前只有字表）。
         * 與 canUpload 互斥，所以頁首永遠只有一顆主要按鈕。
         */
        canCreate() {
            return isCreatable(this.currentPurpose);
        },
        pageCount() {
            return Math.ceil(this.total / PER_PAGE) || 1;
        },
        /**
         * 分享狀態的篩選選項。
         *
         * ⚠ **`delisted` 刻意不在裡面**（需求方 2026-09-10 決定）。後端的 `visibility`
         * 只認得那四個值（它們是 `share_scope` + `review_status` 的組合），而「已下架」
         * 是 `status` 欄位的事，不是分享狀態的一種。
         *
         * ⚠ 已知的後果：篩「分享中」時，一份 `public` + 已核准但**已下架**的素材還是會
         * 被列出來，而它那一欄寫著「已下架」。要消掉這個不一致得在後端的 `visibility`
         * 加一個值並讓另外四個排除已下架的——**刻意不做**，因為那一列自己已經標得很清楚
         * （旗標、原因、上架／下架按鈕的三態）。
         */
        visibilityItems() {
            return VISIBILITIES.map((v) => ({
                value: v,
                text: this.$t(`media.visibility.${v || 'all'}`),
            }));
        },
        orderItems() {
            return ORDERS.map((v) => ({
                value: v,
                text: this.$t(`media.library.order_${v}`),
            }));
        },
        quotaPercent() {
            if (!this.quota || !this.quota.limit_bytes) {
                return 0;
            }
            return Math.min(100, (this.quota.used_bytes / this.quota.limit_bytes) * 100);
        },
        quotaText() {
            if (!this.quota) {
                return '';
            }
            return this.$t('media.library.quota_value', {
                used: formatBytes(this.quota.used_bytes),
                limit: `${USER_QUOTA_GB} GB`,
            });
        },
    },
    created() {
        this.reload(1);
        this.loadCounts();
        this.loadQuota();
    },
    methods: {
        /** 換大類一定要把 chip 歸零：新大類的 chips 是另一組，索引留著會指到錯的。 */
        selectGroup(gi) {
            if (gi === this.groupIndex) {
                return;
            }
            this.groupIndex = gi;
            this.chipIndex = 0;
            this.reload(1);
        },

        selectChip(ci) {
            if (ci === this.chipIndex) {
                return;
            }
            this.chipIndex = ci;
            this.reload(1);
        },

        /** 大類的筆數等於它底下所有 purpose 的和。缺 key 要當 0。 */
        groupCount(group) {
            if (!this.counts) {
                return 0;
            }
            return group.purposes.reduce(
                (sum, p) => sum + (this.counts.counts[p] || 0), 0);
        },

        /** 影片的 chip 讀的是 provider 的細分，不是 counts[purpose]。 */
        chipCount(chip) {
            if (!this.counts) {
                return 0;
            }
            if (chip.provider === 'youtube') {
                return this.counts.video_youtube || 0;
            }
            if (chip.provider === 'uploaded') {
                return this.counts.video_uploaded || 0;
            }
            return this.counts.counts[chip.purpose] || 0;
        },

        /** 影片的 chip 用來源的字（自行上傳／YouTube），其餘用分類名。 */
        chipLabel(chip) {
            if (chip.provider === 'youtube') {
                return this.$t('media.provider.youtube');
            }
            if (chip.provider === 'uploaded') {
                /// 中性的「上傳的影片」而不是「自行上傳」：這是來源類型的篩選，
                /// 與挑選器同一個 chip 該是同一個字（那裡的「公開」分頁不是自己的）。
                return this.$t('media.provider.uploaded');
            }
            return this.$t(`media.purpose.${chip.purpose}`);
        },

        /**
         * 這一份素材的分享狀態。
         *
         * ⚠ **已下架（不論是自己下架還是平台下架）一律回 `delisted`**，蓋過 share_scope
         * 與審核狀態。理由是那兩個值這時候都不成立了——一份已下架的素材即使
         * `share_scope = 'public'` 且已核准，別人也挑不到它，欄位寫「分享中」是在騙老師
         * （2026-09-10）。
         *
         * ⚠ 這一欄**不區分**是誰下架的，那個區別由標題旁的旗標負責
         * （「已下架」vs「已被平台下架」）。同一件事分兩欄講只會讓兩欄都變長。
         *
         * share_scope 是 public 時底下有三種完全不同的處境：還在等審核、已經公開、
         * 被退回。只看 share_scope 的話「分享中」會包含另外兩種，而那兩種別人根本
         * 看不到，老師會以為自己已經分享成功。
         *
         * ⚠ 這一組值與工具列的篩選共用同一批 i18n key，但**`delisted` 不在篩選的選項裡**
         * （依需求方 2026-09-10 決定不加）——見下方 `visibilityItems` 的說明。
         */
        visibilityOf(item) {
            if (isBlocked(item)) {
                return 'delisted';
            }
            if (item.share_scope !== 'public') {
                return 'private';
            }
            if (item.review_status === 'approved') {
                return 'shared';
            }
            if (item.review_status === 'rejected') {
                return 'rejected';
            }
            return 'pending';
        },

        clearFilter() {
            this.keyword = '';
            this.visibility = '';
            this.reload(1);
        },

        async reload(page) {
            this.page = page || 1;
            this.isLoading = true;
            this.loadError = '';
            try {
                const res = await mediaServices.getMediaList({
                    purpose: this.currentPurpose,
                    scope: 'mine',
                    /// 管理頁不做語言過濾：老師要看到自己全部的素材，而不是「符合目前
                    /// 介面語言的那些」。
                    lang: null,
                    page: this.page,
                    perPage: PER_PAGE,
                    /// 一定要帶：管理頁的重點之一就是看轉檔狀態（規格 6.3）。
                    includeProcessing: true,
                    /// 一定要帶，否則「重新上架」永遠按不到：預設查詢只撈 active，
                    /// 所以一份素材被下架之後就從這一頁消失、老師再也找不到它。
                    /// 只有這一頁該帶（後端也只在 scope=mine 時理它）。
                    includeDelisted: true,
                    q: this.keyword || null,
                    /// 只有影片的 chips 帶得出 provider，其餘 chip 是 null。
                    provider: this.currentProvider,
                    visibility: this.visibility || null,
                    order: this.order,
                });
                this.items = (res && res.items) || [];
                this.total = (res && res.total) || 0;
            } catch (err) {
                this.items = [];
                this.total = 0;
                this.loadError = this.$t('media.error.load_failed');
            } finally {
                this.isLoading = false;
            }
            /// 篩選條件變了就要重算筆數：筆數跟著 q 與 visibility 走（見後端
            /// listCounts 的說明），不重算的話 chip 說 4、清單只有 1。
            this.loadCounts();
        },

        async loadCounts() {
            try {
                this.counts = await mediaServices.getMediaCounts({
                    q: this.keyword || null,
                    visibility: this.visibility || null,
                });
            } catch (err) {
                /// 筆數載不到不該擋住整頁——tab 上少幾個數字，清單照樣能用
                this.counts = null;
            }
        },

        async loadQuota() {
            try {
                this.quota = await mediaServices.getMediaQuota();
            } catch (err) {
                /// 配額載不到不該擋住整頁——沒有進度條也還是能管理素材
                this.quota = null;
            }
        },

        openEdit(item) {
            this.editing = item;
            this.editOpen = true;
        },

        /**
         * 開字表對話框。item 為 null 是建立、有值是編輯。
         * 一定要在開啟之前設好 editingWordlist：對話框的 show watcher 是 immediate 的，
         * 它 reset 時會讀 media，順序反了會讀到上一次的值。
         */
        openWordlist(item) {
            this.editingWordlist = item;
            this.wordlistOpen = true;
        },

        /** 同 openWordlist：一定要在開啟之前設好，對話框的 watcher 是 immediate。 */
        openHotspot(item) {
            this.editingHotspot = item;
            this.hotspotOpen = true;
        },

        /** 熱區的唯讀預覽。同上：先設 item 再開。 */
        openPreview(item) {
            this.previewItem = item;
            this.previewOpen = true;
        },

        /** 同上：先設 item 再開（對話框的 watcher 是 immediate，而它會立刻去查 impact）。 */
        openDelist(item) {
            this.delistItem = item;
            this.delistOpen = true;
        },

        /**
         * 下架成功。
         * 一定要重讀清單：那一列的按鈕要換成「重新上架」、標籤要出現。
         * 也要重讀配額——下架不會釋放容量（檔案還在 S3），但重讀比讓老師猜
         * 「數字是不是舊的」好。
         */
        async onDelisted(res) {
            this.$refs.snack?.showAlertMsg('success', this.$t('media.delist.done', {
                q: res.blocked_quest_count, u: res.blocked_unit_count,
            }));
            await this.reload(this.page);
        },

        /**
         * 重新上架。沒有二次確認，恢復不是不可逆的動作。
         *
         * still_blocked_quest_count 一定要顯示出來。一題可能同時被兩份素材卡住
         * （翻卡牌用了兩張圖、兩張都被下架），只恢復其中一份時那題還不會開放。
         * 顯示「已全部恢復」而它不是 0，就是在騙老師。
         */
        async doRelist(item) {
            if (this.relistingId !== null) {
                return;
            }
            this.relistingId = item.media_id;
            try {
                const res = await mediaServices.relistMedia(item.media_id);
                const partial = res.still_blocked_quest_count > 0;
                const key = partial ? 'media.relist.done_partial' : 'media.relist.done';
                this.$refs.snack?.showAlertMsg(
                    partial ? 'warning' : 'success',
                    this.$t(key, {
                        q: res.restored_quest_count,
                        s: res.still_blocked_quest_count,
                    }),
                    /// 「還有 N 道題目被卡住」是老師必須讀到的資訊，預設的 2 秒讀不完，
                    /// 所以那一種給 8 秒。
                    partial ? 8000 : 2000);
                await this.reload(this.page);
            } catch (err) {
                console.log('relistMedia failed:', err && err.message);
                this.$refs.snack?.showAlertMsg('error', this.$t('media.relist.err_failed'));
            } finally {
                this.relistingId = null;
            }
        },

        /** 對話框存好之後重讀清單——排序與狀態標籤都可能因為這次修改而變。 */
        async onEdited() {
            this.$refs.snack?.showAlertMsg('success', this.$t('media.library.saved'));
            await this.reload(this.page);
        },

        async onUploaded() {
            this.$refs.snack?.showAlertMsg('success', this.$t('media.library.uploaded'));
            await this.reload(1);
            await this.loadQuota();
        },

        sizeOf(item) {
            return formatBytes(item.byte_size);
        },

        /**
         * 上傳日期。後端回的是 ISO 字串（驅動的 Date 經過 JSON 序列化），這裡只取
         * 日期部分——hover 提示要回答的是「哪一天上傳的」。
         */
        dateOf(item) {
            const raw = item.create_date;
            if (!raw) {
                return '';
            }
            const d = new Date(raw);
            if (isNaN(d.getTime())) {
                /// 解析不了就原樣顯示，總比顯示 Invalid Date 好
                return String(raw).slice(0, 10);
            }
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        },

        providerOf,
        isBanned,
        banReasonOf,
    },
};

/** bytes 轉成人看得懂的字串。0 與 null 都回 `0 B`。 */
function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) {
        return `${n} B`;
    }
    if (n < 1024 * 1024) {
        return `${(n / 1024).toFixed(1)} KB`;
    }
    if (n < 1024 * 1024 * 1024) {
        return `${(n / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
</script>

<style scoped>
/* 色值集中在這裡對照設計稿，改色請一起改：
   主色 #1A73E8 / hover #1558C0 / 危險 #C5221F / 邊框 #DADCE0
   次要文字 #5F6368 / 淡色文字 #80868B / 主文字 #202124
   列表分隔線 #F1F3F4 / hover 底色 #F8F9FA */

.ml {
    max-width: 1180px;
}

/* 頁首 */
.ml-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
}

.ml-title {
    font-size: 26px;
    font-weight: 700;
    color: #202124;
    line-height: 1.3;
}

.ml-sub {
    font-size: 14px;
    color: #5F6368;
    margin-top: 6px;
}

.ml-quota {
    width: 220px;
}

.ml-quota-label {
    font-size: 12px;
    color: #5F6368;
}

.ml-quota-value {
    font-size: 12px;
    color: #3C4043;
    margin: 2px 0 5px;
}

.ml-quota-bar {
    height: 5px;
    border-radius: 3px;
    background: #E8EAED;
    overflow: hidden;
}

.ml-quota-fill {
    height: 100%;
    border-radius: 3px;
    background: #1A73E8;
    transition: width .2s;
}

/* 快滿了要看得出來：2GB 用完之後上傳會失敗，那時才發現太晚 */
.ml-quota-fill--full {
    background: #C5221F;
}

/* 大類 tab 與 chips */
.ml-groups {
    display: flex;
    gap: 4px;
    margin-top: 20px;
    border-bottom: 1px solid #DADCE0;
}

.ml-group {
    position: relative;
    padding: 10px 16px 9px;
    font-size: 16px;
    color: #5F6368;
    background: none;
    border: none;
}

.ml-group--on {
    font-weight: 700;
    color: #1A73E8;
}

/* 底線畫在按鈕上而不是用 ::after 的 border，這樣才蓋在 .ml-groups 的分隔線之上 */
.ml-group--on::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
    background: #1A73E8;
}

.ml-group-n {
    font-size: 14px;
    font-weight: 400;
    color: #80868B;
    margin-left: 4px;
}

.ml-group-n--on {
    color: #1A73E8;
}

.ml-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 14px 0 0;
}

.ml-chip {
    padding: 6px 14px;
    border-radius: 16px;
    border: 1px solid #DADCE0;
    background: #fff;
    color: #3C4043;
    font-size: 14px;
}

.ml-chip--on {
    background: #E8F0FE;
    border-color: #C6DAFC;
    color: #1558C0;
    font-weight: 500;
}

.ml-chip-n {
    margin-left: 6px;
    color: #80868B;
}

.ml-chip--on .ml-chip-n {
    color: #1558C0;
}

/* 工具列 */
.ml-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0 4px;
    flex-wrap: wrap;
}

.ml-search {
    max-width: 300px;
}

.ml-sel {
    max-width: 190px;
}

/* 統一 40px：三個 Vuetify 元件的預設高度各不相同 */
.ml-toolbar >>> .v-input__slot {
    min-height: 40px !important;
}

.ml-toolbar >>> .v-input__prepend-inner,
.ml-toolbar >>> .v-input__append-inner {
    margin-top: 8px !important;
}

.ml-toolbar >>> .v-label {
    top: 10px !important;
}

.ml-count {
    font-size: 14px;
    color: #5F6368;
}

/* 靠右的是按鈕，不是「共 N 筆」：margin-left:auto 放在這裡，前面四個元件才會
   靠左集中在一起。
   height 要跟旁邊的搜尋框與兩個下拉一致（都是 40px），否則同一行的元件會參差。 */
.ml-primary {
    margin-left: auto;
    height: 40px !important;
    color: #fff !important;
    border-radius: 5px;
    font-size: 14px;
    letter-spacing: 0;
}

/* 列表 */
.ml-body {
    margin-top: 8px;
}

.ml-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 60px 0;
}

.ml-state-msg {
    font-size: 13px;
    color: #5F6368;
    text-align: center;
    max-width: 420px;
}

/* 表頭與列用同一組 flex 設定，欄寬才對得上。
   一定要 flex-wrap：可用寬度不夠時要重排而不是把欄位壓成 0 寬。
   側欄在 960px 以上變成常駐（吃掉 256px），所以可用寬度不是隨視窗單調變化的
   ——900px 視窗有 860px 可用，960px 視窗只有 664px。單一個 max-width 的斷點
   表達不出「窄」，所以靠重排而不是靠斷點切版。 */
.ml-thead,
.ml-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.ml-thead {
    padding: 0 4px 8px;
    border-bottom: 1px solid #E8EAED;
}

.ml-th {
    font-size: 12px;
    font-weight: 500;
    color: #80868B;
    letter-spacing: .5px;
}

.ml-col-thumb {
    flex: 0 0 72px;
}

/* min-width 是重點：沒有它的話兩側的固定欄會把這一欄壓到接近 0，
   標題就變成一字一行（2026-09-08 的手機回報）。 */
.ml-col-info {
    flex: 1 1 240px;
    min-width: 200px;
}

/* 內容寬而不是固定 120px：那 120px 在窄畫面是純浪費，而它讓標題欄更擠。
   min-width 只是為了四個值（私有／審核中／分享中／已退回）不要換行。 */
.ml-col-vis {
    flex: 0 0 auto;
    min-width: 76px;
    display: flex;
    align-items: center;
    gap: 6px;
}

/* min-width 而不是固定寬：找不同的圖有四顆按鈕，超過 300px 會被裁掉。
   同一個分類的按鈕組相同，所以列與列之間仍然對齊。
   flex-basis 給 300px、可收縮但不放大：寬螢幕時就是 300px，多出來的空間全給
   標題欄（那一欄用得到，按鈕不需要）；空間不足時它先收縮，裡面的按鈕靠自己的
   flex-wrap 排成兩排，而不是被擠出畫面。 */
.ml-col-acts {
    flex: 0 1 300px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

/* 表頭的「操作」置中，但按鈕本身仍然靠右（.ml-col-acts 的 justify-content）。
   兩者刻意不同：標籤置中是為了讓它落在那一欄的中間，按鈕靠右是為了讓所有列的
   最後一顆對齊在同一條邊上。 */
.ml-th.ml-col-acts {
    justify-content: center;
}

.ml-row {
    padding: 14px 4px;
    border-bottom: 1px solid #F1F3F4;
}

.ml-row:hover {
    background: #F8F9FA;
}

.ml-thumb {
    width: 72px;
    height: 54px;
    border-radius: 5px;
    border: 1px solid #E0E3E7;
    background: #F1F3F4;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ml-name {
    font-size: 16px;
    font-weight: 500;
    color: #202124;
    /* 長檔名要折行而不是裁掉：care_corner_video 這種沒有空白的字串只有
       break-all 折得掉。 */
    word-break: break-all;
    display: inline-block;
}

.ml-desc {
    font-size: 13px;
    color: #5F6368;
    line-height: 1.6;
    margin-top: 3px;
    /* 最多兩行。需要 -webkit- 前綴，這是唯一被廣泛支援的寫法 */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
}

.ml-flag {
    display: inline-block;
    font-size: 11px;
    font-weight: 400;
    padding: 1px 6px;
    border-radius: 3px;
    margin-left: 6px;
    vertical-align: 2px;
}

.ml-flag--bad {
    background: #FCE8E6;
    color: #C5221F;
}

.ml-flag--warn {
    background: #FEF7E0;
    color: #B06000;
}

.ml-note {
    font-size: 12px;
    color: #5F6368;
    line-height: 1.6;
    margin-top: 4px;
}

.ml-note--bad {
    color: #C5221F;
}

.ml-tip {
    font-size: 12px;
    line-height: 1.6;
}

.ml-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9AA0A6;
    flex: 0 0 auto;
}

.ml-vis-txt {
    font-size: 13px;
    color: #3C4043;
}

/* 操作按鈕 */
.ml-btn {
    height: 34px;
    padding: 0 14px;
    font-size: 13px;
    border-radius: 5px;
    border: 1px solid #DADCE0;
    background: #fff;
    color: #3C4043;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
}

.ml-btn:hover {
    background: #F1F3F4;
}

.ml-btn--open {
    color: #1A73E8;
}

.ml-btn--open:hover {
    background: #E8F0FE;
    border-color: #C6DAFC;
}

/* 灰框紅字，不是紅框：四列都是紅粗框時，「危險」這個訊號會因為到處都是而失效。 */
.ml-btn--danger {
    color: #C5221F;
}

.ml-btn--danger:hover {
    background: #FCE8E6;
    border-color: #F5C9C5;
}

.ml-btn--ok {
    color: #188038;
}

.ml-btn--ok:hover {
    background: #E6F4EA;
    border-color: #B7E1C1;
}

.ml-btn:disabled {
    opacity: .5;
}

.ml-foot {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}

/* 可用寬度不足 800px 時，操作欄整塊移到第二行。
   一列的「固定家具」是縮圖 72px 加按鈕 300px，共 372px；在 660px 的可用寬度裡
   那是一半以上，標題欄只剩 200px 出頭（2026-09-08 回報的 700–800px 太擠）。
   把按鈕放到自己一行之後，第一行的標題欄就有 480px 以上。

   兩個 clause 是因為可用寬度不隨視窗單調變化：側欄在 960px 以上常駐、吃掉 256px。
     視窗 < 960：可用 = 視窗 - 40（容器 padding）→ 840px 以下即 800px 以下
     視窗 >= 960：可用 = 視窗 - 296        → 1095px 以下即 800px 以下
   所以 841–959px 與 1096px 以上才是四欄同一行。只寫一個 max-width 的話，
   在大螢幕把視窗拉到 1000px 一樣會擠，而那不是手機。 */
@media (max-width: 839px), (min-width: 960px) and (max-width: 1095px) {
    /* 表頭只剩三欄。「操作」那一格藏起來而不是讓它也掉到第二行——
       一行只寫著「操作」的表頭讀起來莫名其妙，而按鈕本身看得出是什麼。 */
    .ml-th.ml-col-acts {
        display: none;
    }

    /* basis 100% 讓它自己換到下一行，仍然靠右（justify-content 沿用上面的設定）。
       DOM 順序已經是最後一個，所以不需要 order。 */
    .ml-col-acts {
        flex: 1 1 100%;
    }
}

/* 手機。斷點取 599px：那以下可用寬度不到 560px，連「縮圖 + 標題 + 分享狀態」
   都排不舒服，所以連表頭一起收掉、縮圖也縮小。 */
@media (max-width: 599px) {
    /* 欄位已經不對齊了，表頭只會誤導。手機上每一列讀起來是一張卡片，
       縮圖、標題、狀態、按鈕本身就看得出是什麼。 */
    .ml-thead {
        display: none;
    }

    .ml-row {
        align-items: flex-start;
        gap: 12px;
        padding: 14px 0;
    }

    /* 縮圖縮小，把寬度讓給標題 */
    .ml-col-thumb {
        flex: 0 0 56px;
    }

    .ml-thumb {
        width: 56px;
        height: 42px;
    }

    /* 手機上讓標題吃滿剩餘寬度，不再保留 200px 下限
       （那個下限是為了防止被固定欄壓扁，這裡的固定欄只剩縮圖與狀態）。 */
    .ml-col-info {
        flex: 1 1 auto;
        min-width: 0;
    }

    /* 按鈕已經在自己一行（上面那一段），手機上改成靠左：整行只有按鈕時
       靠左比靠右好按，也符合由左往右讀的順序。 */
    .ml-col-acts {
        justify-content: flex-start;
    }

    /* 搜尋框吃滿一行：300px 在手機上會留一段沒用的空白，而且點擊區域太小 */
    .ml-search {
        max-width: none;
        flex: 1 1 100%;
    }

    /* 兩個下拉各佔一半，不要一個一行 */
    .ml-sel {
        flex: 1 1 0;
        max-width: none;
    }

    /* 標題與儲存空間在手機上是上下兩段，不要並排擠壓 */
    .ml-head {
        gap: 12px;
    }

    .ml-quota {
        width: 100%;
    }
}

/* 說明 */
.ml-legend {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    padding: 14px 16px;
    background: #F8F9FA;
    border: 1px solid #E8EAED;
    border-radius: 6px;
}

.ml-legend-icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #5F6368;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    font-style: italic;
    margin-top: 1px;
}

.ml-legend-txt {
    font-size: 13px;
    color: #3C4043;
    line-height: 1.7;
}
</style>
